import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Title } from 'react-admin';
import { Box, Button, Container, MenuItem, TextField } from '@material-ui/core';
import { RoundType } from '../models/RoundType';
import { FixtureListItem } from '../CreateRound/CreateRoundModels';
import CreateRoundUtil from '../CreateRound/CreateRoundUtil';
import RoundOverviewUtil from '../RoundOverview/RoundOverviewUtil';
import ApiNgUtil from '../api/ApiNgUtil';
import InvokeApiMethods from '../ExternalApi/ExternalApiModels';
import ChooseFixtureDialog from './ChooseFixtureDialog';
import {
    THE_GAME_ROUND_TYPE_PREFIX,
    THE_GAME_ROUND_TYPE_WITH_CORNERS_AND_CARDS,
    TheGameRoundEntry,
    TheGameRoundTestCreateRequest
} from './TheGameModels';

interface CreateTheGameRoundState {
    roundTypeName: string;
    player1: string;
    player2: string;
    overUnderLine: string;
    overUnderHTLine: string;
    cornersOverUnderLine: string;
    cardsOverUnderLine: string;
    jackpotEUR: string;
}

type FieldErrors = Partial<Record<keyof CreateTheGameRoundState | "fixture", string>>;

const parseLine = (value: string): number | undefined => {
    var line = Number(value.trim());
    if (value.trim().length === 0 || !Number.isFinite(line) || line <= 0 || (line * 2) % 2 !== 1) {
        return undefined;
    }
    return line;
}

const formatPromotionValue = (value: string): string => {
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch (error) {
        return value;
    }
}

const CreateTheGameRound: React.FC = () => {
    const [roundTypes, setRoundTypes] = useState<RoundType[]>([]);
    const [fixture, setFixture] = useState<FixtureListItem | undefined>(undefined);
    const [formState, setFormState] = useState<CreateTheGameRoundState>({
        roundTypeName: "",
        player1: "",
        player2: "",
        overUnderLine: "2.5",
        overUnderHTLine: "0.5",
        cornersOverUnderLine: "9.5",
        cardsOverUnderLine: "4.5",
        jackpotEUR: "1000"
    });
    const [errors, setErrors] = useState<FieldErrors>({});
    const [saving, setSaving] = useState<boolean>(false);
    const [saveError, setSaveError] = useState<string>("");
    const [created, setCreated] = useState<TheGameRoundEntry | null>(null);
    const history = useHistory();

    useEffect(() => {
        CreateRoundUtil.getRoundTypes().then((rts: RoundType[]) => {
            setRoundTypes(rts.filter(rt => rt.parameters?.name?.startsWith(THE_GAME_ROUND_TYPE_PREFIX)));
        }).catch(error => {
            setSaveError(CreateRoundUtil.getExceptionMessage(error, false, "GetRoundTypes error"));
        });
    }, []);

    const withCornersAndCards = formState.roundTypeName === THE_GAME_ROUND_TYPE_WITH_CORNERS_AND_CARDS;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormState({ ...formState, [name]: value });
    };

    const onCloseFixtureDlg = (item: FixtureListItem | undefined) => {
        if (item) {
            setFixture(item);
        }
    };

    const toRequest = (): TheGameRoundTestCreateRequest | null => {
        var errs: FieldErrors = {};
        if (formState.roundTypeName.length === 0) {
            errs.roundTypeName = "Required";
        }
        if (!fixture) {
            errs.fixture = "Required";
        }
        if (formState.player1.trim().length === 0) {
            errs.player1 = "Required";
        }
        if (formState.player2.trim().length === 0) {
            errs.player2 = "Required";
        }
        var lineError = "Positive x.5 line";
        var overUnderLine = parseLine(formState.overUnderLine);
        if (overUnderLine === undefined) {
            errs.overUnderLine = lineError;
        }
        var overUnderHTLine = parseLine(formState.overUnderHTLine);
        if (overUnderHTLine === undefined) {
            errs.overUnderHTLine = lineError;
        }
        var cornersOverUnderLine: number | undefined = undefined;
        var cardsOverUnderLine: number | undefined = undefined;
        if (withCornersAndCards) {
            cornersOverUnderLine = parseLine(formState.cornersOverUnderLine);
            if (cornersOverUnderLine === undefined) {
                errs.cornersOverUnderLine = lineError;
            }
            cardsOverUnderLine = parseLine(formState.cardsOverUnderLine);
            if (cardsOverUnderLine === undefined) {
                errs.cardsOverUnderLine = lineError;
            }
        }
        var jackpotEUR: number | undefined = undefined;
        if (formState.jackpotEUR.trim().length > 0) {
            jackpotEUR = Number(formState.jackpotEUR.trim());
            if (!Number.isInteger(jackpotEUR) || jackpotEUR < 0) {
                errs.jackpotEUR = "Non-negative integer";
            }
        }
        setErrors(errs);
        if (Object.keys(errs).length > 0 || !fixture || overUnderLine === undefined || overUnderHTLine === undefined) {
            return null;
        }
        return {
            roundTypeName: formState.roundTypeName,
            fixtureId: parseInt(fixture.id),
            player1: formState.player1.trim(),
            player2: formState.player2.trim(),
            jackpotEUR: jackpotEUR,
            overUnderLine: overUnderLine,
            overUnderHTLine: overUnderHTLine,
            cornersOverUnderLine: cornersOverUnderLine,
            cardsOverUnderLine: cardsOverUnderLine
        };
    };

    const onCreate = () => {
        setSaveError("");
        setCreated(null);
        var request = toRequest();
        if (!request) {
            return;
        }
        setSaving(true);
        ApiNgUtil.getObjectWithBody<TheGameRoundEntry>("the-game/rounds/test", InvokeApiMethods.POST(), JSON.stringify(request)).then(resp => {
            if (!resp.success || !resp.item) {
                setSaveError(`Create TheGame round failed: ${resp.message}`);
                return;
            }
            setCreated(resp.item);
        }).catch(error => {
            setSaveError(CreateRoundUtil.getExceptionMessage(error, false, "Create TheGame round error"));
        }).finally(() => {
            setSaving(false);
        });
    };

    var textField = (name: keyof CreateTheGameRoundState, label: string) => (
        <TextField
            name={name}
            label={label}
            value={formState[name]}
            style={{ width: 180, marginRight: 10 }}
            error={!!errors[name]}
            helperText={errors[name]}
            onChange={handleChange}
            disabled={saving}
        />
    );

    var createdBox = null;
    if (created) {
        createdBox = (
            <Box paddingTop={2} color="blue">
                <Box>{`Round was successfully created [Id:${created.id}] [Type:${created.roundTypeName}] [Status:${created.status}] [OpenTo:${created.openTo}]`}</Box>
                <ol start={0}>
                    {(created.fixtures ?? []).map(f => (
                        <li key={f.id}>{`${f.name} (${f.id})`}</li>
                    ))}
                </ol>
                <Box>Promotion values:</Box>
                {(created.promotionValues ?? []).map(pv => (
                    <Box key={pv.id} paddingTop={1}>
                        <Box fontWeight="bold">{pv.key}</Box>
                        <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{formatPromotionValue(pv.value)}</pre>
                    </Box>
                ))}
                <Box paddingTop={2}>
                    <Button variant="outlined" color="secondary" size="small" onClick={() => history.push('roundOverview')}>
                        Round Overview
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Title title="Create TheGame Round" />
            <Box width="250px">
                <TextField
                    label="Round type"
                    select
                    name="roundTypeName"
                    value={formState.roundTypeName}
                    onChange={handleChange}
                    fullWidth
                    disabled={saving}
                    error={!!errors.roundTypeName}
                    helperText={errors.roundTypeName ?? `${roundTypes.length} TheGame round types`}
                >
                    {roundTypes.map(rt => (
                        <MenuItem key={rt.id} value={rt.parameters.name}>
                            {rt.parameters.name}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>
            <Box paddingTop={2} display="flex" alignItems="center">
                <TextField
                    label="Fixture"
                    value={fixture ? `${fixture.name} (${fixture.id}) ${CreateRoundUtil.toGameDateString(fixture.startingAt)}` : ""}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                    style={{ minWidth: 450, marginRight: 10 }}
                    error={!!errors.fixture}
                    helperText={errors.fixture}
                />
                <ChooseFixtureDialog openLabel={fixture ? "Change" : "Choose Fixture"} onClose={onCloseFixtureDlg} disabled={saving} />
            </Box>
            <Box paddingTop={2}>
                {textField("overUnderLine", "Over/Under goals (FT)")}
                {textField("overUnderHTLine", "Over/Under goals (HT)")}
                {withCornersAndCards ? textField("cornersOverUnderLine", "Corners Over/Under (FT)") : null}
                {withCornersAndCards ? textField("cardsOverUnderLine", "Cards Over/Under (FT)") : null}
            </Box>
            <Box paddingTop={2}>
                {textField("player1", "Player 1")}
                {textField("player2", "Player 2")}
            </Box>
            <Box paddingTop={2}>
                <TextField
                    name="jackpotEUR"
                    label="Jackpot EUR"
                    type="number"
                    value={formState.jackpotEUR}
                    style={{ width: 180 }}
                    InputLabelProps={{ shrink: true }}
                    placeholder="Default"
                    error={!!errors.jackpotEUR}
                    helperText={errors.jackpotEUR ?? "Empty = default"}
                    onChange={handleChange}
                    disabled={saving}
                />
            </Box>
            <Box paddingTop={2}>
                <Button onClick={() => onCreate()} variant="contained" color="primary" disabled={saving}>
                    {saving ? "Creating..." : "Create"}
                </Button>
                {saveError.length > 0 ? (<span style={{ color: RoundOverviewUtil.INVALID_COLOR }}>&nbsp;{saveError}</span>) : null}
            </Box>
            {createdBox}
        </Container>
    );
};

export default CreateTheGameRound;
